import {Component, EventEmitter, forwardRef, Input, OnInit, Output} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

@Component({
  selector: 'ng-time-picker',
  standalone: true,
  imports: [
    DecimalPipe
  ],
  templateUrl: './time-picker.html',
  styleUrls: ['./time-picker.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true
    }
  ]
})
export class TimePickerComponent implements ControlValueAccessor, OnInit {
  @Input('minute-step') minuteStep: number = 1;
  @Input() format: string = 'hh:mm A';
  @Output() timeChange = new EventEmitter<string>();
  @Input() showPeriod: boolean = true;

  hour: number = 12;
  minute: number = 0;
  period: 'AM' | 'PM' = 'AM';

  // ControlValueAccessor properties
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  private _value: string = '';

  ngOnInit(): void {
    // If showPeriod is false, we should probably use a 24-hour format.
    if (!this.showPeriod && this.format === 'hh:mm A') {
      this.format = 'HH:mm';
    }
    this.parseValue(this._value);
  }

  writeValue(value: string): void {
    this._value = value || '';
    this.parseValue(this._value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private parseValue(value: string): void {
    const time = dayjs(value, this.format);
    if (time.isValid()) {
      this.hour = parseInt(time.format(this.showPeriod ? 'hh' : 'HH'), 10);
      if (this.showPeriod && this.hour === 0) this.hour = 12; // dayjs hh format is 01-12, handle 00 case for 12 AM
      this.minute = time.minute();
      this.period = time.format('A') as 'AM' | 'PM';
    }
  }

  incrementHour(): void {
    this.hour = (this.hour % 12) + 1;
    this.emitTime();
  }

  decrementHour(): void {
    this.hour = this.hour === 1 ? 12 : this.hour - 1;
    this.emitTime();
  }

  incrementMinute(): void {
    const remainder = this.minute % this.minuteStep;
    let newMinute: number;
    if (remainder === 0) {
      newMinute = this.minute + this.minuteStep;
    } else {
      newMinute = this.minute - remainder + this.minuteStep;
    }

    if (newMinute >= 60 && this.minute < 60) { // Check minute < 60 to prevent double increment
      this.incrementHour();
    }
    this.minute = newMinute % 60;
    this.emitTime();
  }

  decrementMinute(): void {
    const remainder = this.minute % this.minuteStep;
    let newMinute = remainder === 0 ? this.minute - this.minuteStep : this.minute - remainder;

    if (newMinute < 0 && this.minute >= 0) { // Check minute >= 0 to prevent double decrement
      this.decrementHour();
    }
    this.minute = (newMinute % 60 + 60) % 60; // Handles negative results correctly
    this.emitTime();
  }

  togglePeriod(): void {
    this.period = this.period === 'AM' ? 'PM' : 'AM';
    this.emitTime();
  }

  // Optional: Allow direct input from the input fields
  onHourChange(event: Event): void {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    if (!isNaN(value) && value >= 1 && value <= 12) {
      this.hour = value;
      this.emitTime();
    }
  }

  onMinuteChange(event: Event): void {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    if (!isNaN(value) && value >= 0 && value <= 59) {
      this.minute = value;
      this.emitTime();
    }
  }

  private emitTime(): void {
    // Construct a parsable string to build a dayjs object
    const hourForDayjs = this.showPeriod && this.hour === 12 ? (this.period === 'AM' ? 0 : 12) : this.hour;
    const timeStringForDayjs = `${hourForDayjs}:${this.minute} ${this.period}`;

    // Use dayjs to format the output string according to the format input
    const time = dayjs(timeStringForDayjs, 'h:m A');
    const formattedTime = time.format(this.format);

    this.onChange(formattedTime);
    this.onTouched();
    this.timeChange.emit(formattedTime);
  }
}
